import { useTranslation } from 'react-i18next';
import Select from '../Select';
import { I18N_NAMESPACE } from '../constants';
import {
  CONTROLS_SPACING_CLASS_BY_SIZE,
  GO_TO_PAGE_WRAPPER_CLASS_BY_SIZE,
  ITEM_COUNT_LABEL_CLASS_BY_SIZE,
  ITEM_COUNT_WRAPPER_CLASS_BY_SIZE,
  NAV_BUTTON_PADDING_CLASS_BY_SIZE,
  SELECT_WIDTH_CLASS_BY_SIZE,
} from './Paginator.constants';
import {
  buildPaginationItems,
  createBreakClass,
  createNavButtonClass,
  createNumericOptions,
  normalizePageCount,
} from './Paginator.utilities';
import type { SingleValue } from 'react-select';
import type { NumericOption, PaginatorLabel, PaginatorSize, PaginatorVariant } from './Paginator.types';

const PREVIOUS_CHEVRON = '\u2039'; // char: ‹
const NEXT_CHEVRON = '\u203a'; // char: ›

type PaginatorProps = {
  /**
   * Total number of pages available.
   */
  'pageCount': number;

  /**
   * Currently selected 1-based page number.
   */
  'pageNumber': number;

  /**
   * Current page size. When omitted, the page-size selector is hidden.
   */
  'pageSize'?: number;

  /**
   * Visual variant for the paginator layout. Defaults to `"normal"`.
   */
  'variant'?: PaginatorVariant;

  /**
   * Number of pages shown around the current page in the middle window.
   * Defaults to `2`, or `1` when `variant` is `"compact"`.
   */
  'pageRangeDisplayed'?: number;

  /**
   * Number of pages always shown at the beginning and end of the paginator.
   * Defaults to `2`, or `1` when `variant` is `"compact"`.
   */
  'marginPagesDisplayed'?: number;

  /**
   * Total number of items across all pages.
   */
  'total'?: number;

  /**
   * Custom options for the page-size selector.
   */
  'pageSizeOptions'?: number[];

  /**
   * Visual size for the paginator controls.
   */
  'size'?: PaginatorSize;

  /**
   * Allows new page-size values to be entered in the page-size selector.
   */
  'creatablePageSizes'?: boolean;

  /**
   * Overrides the default translated label shown before the total item count.
   */
  'totalLabel'?: PaginatorLabel;

  /**
   * Overrides the default translated label shown before the page selector.
   */
  'pageLabel'?: PaginatorLabel;

  /**
   * Overrides the default translated label shown before the page-size selector.
   */
  'showLabel'?: PaginatorLabel;

  /**
   * Overrides the default translated label shown in the previous-page button.
   */
  'previousLabel'?: PaginatorLabel;

  /**
   * Overrides the default translated ARIA label announced for the previous-page button.
   */
  'previousAriaLabel'?: string;

  /**
   * Overrides the default translated label shown in the next-page button.
   */
  'nextLabel'?: PaginatorLabel;

  /**
   * Overrides the default translated ARIA label announced for the next-page button.
   */
  'nextAriaLabel'?: string;

  /**
   * Overrides the default translated label used for collapsed page ranges.
   */
  'breakLabel'?: PaginatorLabel;

  /**
   * Test id applied to the paginator root element.
   */
  'testId'?: string;

  /**
   * Class applied to each numbered page list item.
   */
  'pageClassName'?: string;

  /**
   * Class applied to each numbered page button.
   */
  'pageLinkClassName'?: string;

  /**
   * Class applied to the previous-page list item.
   */
  'previousClassName'?: string;

  /**
   * Class applied to the previous-page button.
   */
  'previousLinkClassName'?: string;

  /**
   * Class applied to the next-page list item.
   */
  'nextClassName'?: string;

  /**
   * Class applied to the next-page button.
   */
  'nextLinkClassName'?: string;

  /**
   * Class applied to the active numbered page list item.
   */
  'activeClassName'?: string;

  /**
   * Class applied to the pagination list container element.
   */
  'containerClassName'?: string;

  /**
   * ARIA label applied to the nav element for improved accessibility. Defaults to a translated string "Pagination".
   */
  'aria-label'?: string;

  /**
   * Called when the user requests a different 1-based page number.
   */
  'onPageChange': (page: number) => void;

  /**
   * Updates the page size when the page-size selector changes.
   */
  'onPageSizeChange'?: (size: number) => void;
};

const Paginator = ({
  onPageChange,
  pageCount: pageCountProp,
  pageNumber,
  pageSize,
  onPageSizeChange = () => undefined,
  variant = 'normal',
  pageRangeDisplayed,
  marginPagesDisplayed,
  total,
  pageSizeOptions,
  size = 'normal',
  creatablePageSizes = false,
  totalLabel: totalLabelProp,
  pageLabel: pageLabelProp,
  showLabel: showLabelProp,
  previousLabel: previousLabelProp,
  previousAriaLabel: previousAriaLabelProp,
  nextLabel: nextLabelProp,
  nextAriaLabel: nextAriaLabelProp,
  breakLabel: breakLabelProp,
  pageClassName = '',
  pageLinkClassName = '',
  previousClassName = '',
  previousLinkClassName = '',
  nextClassName = '',
  nextLinkClassName = '',
  activeClassName = '',
  containerClassName = '',
  'aria-label': arialLabel,
  testId = 'paginator',
}: PaginatorProps) => {
  const { t } = useTranslation(I18N_NAMESPACE);
  const isCompact = variant === 'compact';

  const totalLabel = totalLabelProp ?? t('PAGINATOR.TOTAL', { defaultValue: 'Total:' });
  const pageLabel = pageLabelProp ?? t('PAGINATOR.PAGE', { defaultValue: 'Page:' });
  const showLabel = showLabelProp ?? t('PAGINATOR.SHOW', { defaultValue: 'Show:' });
  const previousLabel = previousLabelProp ?? t('PAGINATOR.PREVIOUS', { defaultValue: 'Previous' });
  const previousAriaLabel = previousAriaLabelProp ?? t('PAGINATOR.PREVIOUS', { defaultValue: 'Previous' });
  const nextLabel = nextLabelProp ?? t('PAGINATOR.NEXT', { defaultValue: 'Next' });
  const nextAriaLabel = nextAriaLabelProp ?? t('PAGINATOR.NEXT', { defaultValue: 'Next' });
  const breakLabel = breakLabelProp ?? t('PAGINATOR.BREAK', { defaultValue: '...' });
  const ariaLabel = arialLabel ?? t('PAGINATOR.ARIA_LABEL', { defaultValue: 'Pagination' });
  const pageCount = normalizePageCount(pageCountProp);

  const hasPageSize = pageSize !== null && pageSize !== undefined;
  const resolvedPageSizeOptions = hasPageSize ? (pageSizeOptions ?? [5, 10, 15, 20, 50]) : [];
  const formattedOptions = createNumericOptions(resolvedPageSizeOptions);
  const formattedGoToPageOptions = createNumericOptions(pageCount);
  const currentPage = pageCount > 0 ? Math.min(Math.max(pageNumber, 1), pageCount) : 1;
  const resolvedPageRangeDisplayed = pageRangeDisplayed ?? (isCompact ? 1 : 2);
  const resolvedMarginPagesDisplayed = marginPagesDisplayed ?? (isCompact ? 1 : 2);
  const paginationItems = buildPaginationItems({
    currentPage,
    pageCount,
    pageRangeDisplayed: resolvedPageRangeDisplayed,
    marginPagesDisplayed: resolvedMarginPagesDisplayed,
  });

  const selectWidthClass = SELECT_WIDTH_CLASS_BY_SIZE[size];
  const controlsSpacingClass = CONTROLS_SPACING_CLASS_BY_SIZE[size];
  const navButtonPaddingClass = NAV_BUTTON_PADDING_CLASS_BY_SIZE[size];

  const handleGoToPageChange = (option: SingleValue<NumericOption>) => {
    if (option) {
      onPageChange(option.value);
    }
  };

  const handlePageSizeChange = (option: SingleValue<NumericOption>) => {
    if (option) {
      onPageSizeChange(option.value);
    }
  };

  return (
    <div
      className={`tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-2 tw:text-sq-text-color tw:dark:text-sq-dark-text ${
        isCompact ? 'tw:flex-1 tw:justify-between' : 'tw:justify-end'
      }`}
      data-testid={testId}>
      <div className={`tw:flex tw:flex-nowrap tw:items-center ${controlsSpacingClass}`}>
        <div
          data-testid="goToPageWrapper"
          className={`tw:flex tw:flex-nowrap tw:items-center ${controlsSpacingClass} ${GO_TO_PAGE_WRAPPER_CLASS_BY_SIZE[size]}`}>
          {total !== undefined && (
            <span className="tw:whitespace-nowrap">
              {totalLabel} <strong>{total}</strong>
            </span>
          )}
          <span>{pageLabel}</span>
          <Select
            small={size === 'small'}
            placeholder=""
            menuPortalTarget={typeof document === 'undefined' ? null : document.body}
            extraClassNames={selectWidthClass}
            value={formattedGoToPageOptions.find(({ value }) => value === currentPage)}
            options={formattedGoToPageOptions}
            isSearchable={true}
            onChange={handleGoToPageChange}
          />
        </div>
        {hasPageSize && (
          <div
            data-testid="itemCountWrapper"
            className={`tw:flex tw:flex-nowrap tw:items-center ${controlsSpacingClass} ${ITEM_COUNT_WRAPPER_CLASS_BY_SIZE[size]}`}>
            <span className={ITEM_COUNT_LABEL_CLASS_BY_SIZE[size]}>{showLabel}</span>
            <Select
              small={size === 'small'}
              menuPlacement="auto"
              menuPortalTarget={typeof document === 'undefined' ? null : document.body}
              extraClassNames={selectWidthClass}
              value={formattedOptions.find(({ value }) => value === pageSize)}
              options={formattedOptions}
              isSearchable={false}
              onChange={handlePageSizeChange}
              creatable={creatablePageSizes}
            />
          </div>
        )}
      </div>
      <nav aria-label={ariaLabel} className="tw:flex">
        <ul className={`tw:flex tw:list-none tw:m-0 tw:p-0 tw:items-center ${containerClassName}`.trim()}>
          <li className={previousClassName}>
            <button
              type="button"
              aria-label={previousAriaLabel}
              disabled={currentPage <= 1 || pageCount <= 0}
              onClick={() => onPageChange(currentPage - 1)}
              className={`${createNavButtonClass(navButtonPaddingClass, size, {
                disabled: currentPage <= 1 || pageCount <= 0,
                rounded: 'tw:rounded-l-lg',
              })} ${previousLinkClassName}`.trim()}>
              {isCompact ? (previousLabelProp ?? <span aria-hidden="true">{PREVIOUS_CHEVRON}</span>) : previousLabel}
            </button>
          </li>
          {paginationItems.map((item) => (
            <li
              key={item.type === 'page' ? `page-${item.page}` : item.key}
              className={
                item.type === 'page'
                  ? `${pageClassName} ${item.page === currentPage ? activeClassName : ''}`.trim()
                  : undefined
              }>
              {item.type === 'page' ? (
                <button
                  type="button"
                  aria-current={item.page === currentPage ? 'page' : undefined}
                  aria-label={t('PAGINATOR.PAGE_NUMBER', { page: item.page, defaultValue: 'Page {{page}}' })}
                  onClick={() => {
                    if (item.page !== currentPage) {
                      onPageChange(item.page);
                    }
                  }}
                  className={`${createNavButtonClass(navButtonPaddingClass, size, {
                    active: item.page === currentPage,
                  })} ${pageLinkClassName}`.trim()}>
                  {item.page}
                </button>
              ) : (
                <span aria-hidden="true" className={createBreakClass(navButtonPaddingClass)}>
                  {breakLabel}
                </span>
              )}
            </li>
          ))}
          <li className={nextClassName}>
            <button
              type="button"
              aria-label={nextAriaLabel}
              disabled={currentPage >= pageCount || pageCount <= 0}
              onClick={() => onPageChange(currentPage + 1)}
              className={`${createNavButtonClass(navButtonPaddingClass, size, {
                disabled: currentPage >= pageCount || pageCount <= 0,
                rounded: 'tw:rounded-r-lg',
              })} ${nextLinkClassName}`.trim()}>
              {isCompact ? (nextLabelProp ?? <span aria-hidden="true">{NEXT_CHEVRON}</span>) : nextLabel}
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default Paginator;
